<template>
  <div class="services-wrap">
    <div class="services-content">
      <div :class="['services-header', locale]">
        <span v-viewportChanged="showView" class="hidden-element title">{{ pr.sectionTitle }}</span>
        <span v-viewportChanged="showView" class="hidden-element title-en">{{ pr.sectionTitleEn }}</span>
        <p v-viewportChanged="showView" class="hidden-element description">{{ pr.sectionDescription }}</p>
      </div>

      <div class="services-list">
        <component
          :is="hasServiceRoute(svc) ? 'router-link' : 'div'"
          v-for="svc in pr.services"
          :key="svc.id"
          :to="hasServiceRoute(svc) ? toServiceRoute(svc) : undefined"
          :id="svc.id"
          :class="['service-item', locale, { clickable: hasServiceRoute(svc) }]"
        >
          <div :class="['service-item-content', { 'with-action': !!svc.actionText && hasServiceRoute(svc) }]">
            <div class="service-head">
              <div>
                <div class="service-icon-wrap">
                  <img :src="getImageUrl(svc.icon)" alt="" aria-hidden="true">
                </div>
                <h3 v-viewportChanged="showView" class="hidden-element service-name">{{ svc.name }}</h3>
                <p v-viewportChanged="showView" class="hidden-element service-subtitle">{{ svc.service }}</p>
              </div>
              <img
                v-if="svc.partnerIcon"
                class="service-partner-icon"
                :src="getImageUrl(svc.partnerIcon)"
                alt="" aria-hidden="true"
              >
            </div>
            <p v-viewportChanged="showView" class="hidden-element service-description">{{ svc.description }}</p>
            <div v-if="svc.tags && svc.tags.length" class="service-tags">
              <span v-for="tag in svc.tags" :key="tag" class="service-tag">{{ tag }}</span>
            </div>
            <p v-if="svc.actionText && hasServiceRoute(svc)" class="service-action">
              <span>{{ svc.actionText }}</span>
              <img v-if="svc.actionIcon" :src="getImageUrl(svc.actionIcon)" alt="" aria-hidden="true">
            </p>
          </div>
        </component>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, inject } from 'vue';
import useRenderData from '@/composables/useRenderData';

export default {
  name: 'services',
  setup() {
    const { pr, getImageUrl } = useRenderData('services');
    const locale = inject('locale');
    const localeValue = computed(() => locale?.value || '');

    const hasServiceRoute = (svc) => !!svc.routeName;
    const toServiceRoute = (svc) => {
      if (!svc.routeName) return undefined;
      return {
        name: svc.routeName,
        params: {
          lang: localeValue.value || undefined,
        },
      };
    };

    return {
      pr,
      locale,
      getImageUrl,
      hasServiceRoute,
      toServiceRoute,
    };
  },
};
</script>

<style lang="scss" scoped>
.services-wrap {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 80px 24px;
  background: transparent;
  color: #222222;

  .services-content {
    width: 100%;
    max-width: 1200px;
    display: flex;
    flex-direction: column;
    align-items: center;

    .services-header {
      display: flex;
      flex-direction: column;
      align-items: center;
      text-align: center;

      .title {
        color: #212121;
        font-size: 30px;
        font-weight: bold;
        line-height: 30px;
      }

      .title-en {
        margin-top: 4px;
        color: #666666;
        font-size: 16px;
        line-height: 28px;
      }

      .description {
        margin-top: 14px;
        max-width: 700px;
        color: #222222;
        font-size: 16px;
        line-height: 24px;
      }
    }

    .services-list {
      width: 100%;
      margin-top: 56px;
      display: grid;
      grid-template-columns: 1fr;
      gap: 24px;
    }

    .service-item {
      display: flex;
      text-decoration: none;
      background: #FFFFFF;
      border: 1px solid #E4E9EB;
      color: inherit;

      &.clickable {
        transition:
          transform 300ms cubic-bezier(0.165, 0.84, 0.44, 1),
          box-shadow 300ms cubic-bezier(0.165, 0.84, 0.44, 1);

        &:hover,
        &:active {
          opacity: 1;
          transform: translateY(-4px);
          box-shadow: 0 10px 20px 0 rgba(0, 0, 0, 0.1);

          .service-action {
            color: #0f5ab6;
          }

          .service-action img {
            transform: translateX(4px);
          }
        }
      }
    }

    .service-item-content {
      width: 100%;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      padding: 24px;
      position: relative;

      .service-head {
        width: 100%;
        display: flex;
        justify-content: space-between;
      }

      .service-icon-wrap {
        width: 60px;
        height: 60px;
        border-radius: 9999px;
        background: #F1F3F4;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
          width: 36px;
          height: 36px;
          object-fit: contain;
        }
      }

      .service-partner-icon {
        width: 86px;
        height: 86px;
        align-self: center;
      }

      .service-name {
        margin-top: 18px;
        color: #212121;
        font-size: 18px;
        font-weight: bold;
        line-height: 28px;
      }

      .service-subtitle {
        margin-top: 4px;
        color: #A7AEB1;
        font-size: 12px;
        line-height: 18px;
      }

      .service-description {
        margin-top: 12px;
        color: #666666;
        font-size: 14px;
        line-height: 22px;
      }

      .service-tags {
        margin-top: 16px;
        display: flex;
        flex-wrap: wrap;
        gap: 8px;

        .service-tag {
          background: #F1F3F4;
          color: #666666;
          font-size: 12px;
          line-height: 18px;
          padding: 4px 10px;
          border-radius: 2px;
        }
      }

      .service-action {
        position: absolute;
        right: 24px;
        bottom: 24px;
        display: flex;
        align-items: center;
        color: #1565C0;
        font-size: 12px;
        font-weight: 600;
        line-height: 20px;
        transition: background-color 200ms cubic-bezier(0.02, 0.01, 0.47, 1);

        img {
          width: 12px;
          height: 12px;
          margin-left: 4px;
          filter: brightness(0);
          transition: transform 200ms cubic-bezier(0.02, 0.01, 0.47, 1);
        }
      }

      &.with-action {
        padding-bottom: 58px;
      }
    }

    .service-item.ja {
      .service-description {
        font-family:
          "Hiragino Kaku Gothic ProN", "Hiragino Sans", "Yu Gothic Medium", "Yu Gothic", sans-serif;
      }
    }

    .service-item:not(.clickable) {
      cursor: default;
    }
  }
}

@media only screen and (min-width: 769px) {
  .services-wrap {
    padding-top: 160px;
    padding-bottom: 112px;

    .services-content {
      .services-header {
        .title {
          line-height: 30px;
        }
      }

      .services-list {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }
  }
}

@media only screen and (max-width: 768px) {
  .services-wrap {
    padding: 55px 12px 80px;

    .services-content {
      .services-header {
          &.ja {
          .title {
            line-height: 22px;
          }
        }

        .title {
          font-size: 22px;
        }

        .title-en {
          margin-top: 2px;
          font-size: 16px;
          line-height: 24px;
        }

        .description {
          margin-top: 12px;
          font-size: 14px;
          line-height: 24px;
        }
      }

      .services-list {
        margin-top: 56px;
        gap: 24px;
        grid-template-columns: 1fr;
      }

      .service-item {
        min-height: 0;

        .service-item-content {
          padding: 20px;

          .service-description {
            margin-top: 10px;
          }

          .service-tags {
            margin-top: 14px;
          }

          .service-action {
            bottom: 20px;
            right: 20px;
          }

          &.with-action {
            padding-bottom: 54px;
          }
        }
      }
    }
  }
}
</style>
